import React, { useState, useEffect, useRef } from 'react';
import { Shield, Loader2, MousePointerClick, XCircle } from 'lucide-react';
import { useSearchParams, useNavigate } from "react-router-dom";

// ============================================================================
// [Backend API 호출 로직] 
// ============================================================================
const getBaseUrl = () => ``;
const API_BASE_URL = `/httpApi/web`;

const BackendAPI = {
    reqTransNo: async () => {
        const response = await fetch(`${getBaseUrl()}/api/auth/req-trans-no`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        return response.json();
    },

    reqAuthData: async (transNo, token) => {
        const response = await fetch(`${getBaseUrl()}/api/auth/req-auth-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transNo, token }),
        });
        return response.json();
    },

    saveAppCardData: async (userInfo) => {
        const response = await fetch(`${API_BASE_URL}/appCard`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                gender: userInfo.gender,
                nation: userInfo.nation,
                di: userInfo.di,
                ci: userInfo.ci,
                name: userInfo.name,
                birthDate: userInfo.birthDate,
                phoneNo: userInfo.phoneNo,
                storeIdx: userInfo.storeIdx, 
                hash: userInfo.hash
            }),
        });
        return response.json();
    }
};

// ============================================================================
// [Frontend 화면 및 이벤트 로직] 
// ============================================================================
export default function AppCardLogin() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const storeIdx = searchParams.get("storeIdx");
    const hash = searchParams.get("hash");

    const [step, setStep] = useState('loading'); // UI 상태 (loading, initial)
    const [moduleVisible, setModuleVisible] = useState(false);
    const [authData, setAuthData] = useState({ transNo: '', token: '' });
    const [deviceType, setDeviceType] = useState('5');
    
    const objectRef = useRef(null);
    const APPCARD_MODULE_URL = 'https://payment.tvhub.co.kr:10488/APPCARD-AUTH/index.html#/';

    // 디바이스 판별
    useEffect(() => {
        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        const line = ua.match(/Line/i);

        let detectedType = '5'; // 기본 PC
        if (/android/.test(ua.toLowerCase())) { detectedType = '3'; }
        if (iOSSafari === true) { detectedType = '1'; }
        if (line !== null) { detectedType = '1'; }

        setDeviceType(detectedType);
    }, []);

    // 인증 모듈 닫기
    const closeModule = () => {
        setModuleVisible(false);
        setAuthData({ transNo: '', token: '' });
        setStep('initial'); // 닫았을 시 재시도 화면 노출
    };

    // [변경점 2] 앱카드 인증 강제/수동 시작
    const handleStartLogin = async () => {
        setStep('loading');
        try {
            const res = await BackendAPI.reqTransNo();

            if (res.resultCode === '0000') {
                setAuthData({ transNo: res.resultData.transNo, token: res.resultData.token });
                setModuleVisible(true);
            } else {
                alert(res.resultMessage || '거래번호 발급에 실패했습니다.');
                setStep('initial');
            }
        } catch (err) {
            alert('서버 통신 오류가 발생했습니다.');
            setStep('initial');
        }
    };

    // [변경점 2] 페이지 로드 시 자동 인증 실행 로직 추가
    useEffect(() => {
        if (!storeIdx || !hash) {
            alert("비정상적인 접근입니다.");
            window.close();
            navigate(-1);
            return;
        }

        const timer = setTimeout(() => {
            handleStartLogin();
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeIdx, hash]);

    // 모듈 이벤트 수신 및 결과 처리
    useEffect(() => {
        const handleMessage = async (event) => {
            if (!event.data || !event.data.resultCode) return;

            const { resultCode, resultMessage, scheme } = event.data;

            switch (resultCode) {
                case '0000':
                    setModuleVisible(false);
                    setStep('loading');
                    try {
                        const userInfoRes = await BackendAPI.reqAuthData(authData.transNo, authData.token);
                        if (userInfoRes.resultCode === '0000') {
                            const userData = userInfoRes.resultData;
                            userData.storeIdx = storeIdx;
                            userData.hash = hash;
                            
                            // DB 저장
                            await BackendAPI.saveAppCardData(userData);

                            // [변경점 3] 인증 완료 처리 및 페이지 닫기
                            alert("본인인증이 완료되었습니다.");
                            if (window.opener) {
                                window.close();
                                navigate(-1);
                            } else {
                                window.location.href = "about:blank";
                                window.close();
                                navigate(-1);
                            }
                        } else {
                            alert(userInfoRes.resultMessage || '사용자 정보 조회에 실패했습니다.');
                            setStep('initial');
                        }
                    } catch (err) {
                        alert('사용자 정보를 처리하는 중 오류가 발생했습니다.');
                        setStep('initial');
                    }
                    break;

                case '0001':
                    alert('카드사 앱이 설치되어 있지 않습니다. 스토어로 이동합니다.');
                    if (scheme) window.location.href = scheme;
                    setStep('initial');
                    break;

                case '0002':
                    closeModule();
                    alert(`인증 실패 (${resultMessage})`);
                    break;

                case '0003':
                    closeModule(); // 닫기 버튼
                    break;

                default:
                    closeModule();
                    alert('알 수 없는 오류가 발생했습니다.');
                    break;
            }
        };

        const handleVisibilityChange = () => {
            const state = document.visibilityState;
            if (moduleVisible && objectRef.current && objectRef.current.contentWindow) {
                const hasFocus = state === 'visible';
                const eventMessage = hasFocus ? 'focus' : 'blur';
                objectRef.current.contentWindow.postMessage(
                    JSON.stringify({ message: eventMessage }),
                    '*'
                );
            }
        };

        window.addEventListener('message', handleMessage);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('message', handleMessage);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [authData, moduleVisible, storeIdx, hash, navigate]);

    // [변경점 1] app.js와 완전히 동일한 UI로 교체
    return (
        <div className="certification-view">
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 relative">
                    
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
                            <Shield className="w-8 h-8 text-indigo-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">본인인증</h1>
                        <p className="text-gray-600 text-sm">
                            안전한 서비스 이용을 위해 본인인증이 필요합니다
                        </p>
                    </div>

                    {/* Loading UI */}
                    {step === 'loading' && (
                        <div className="space-y-6 py-8">
                            <div className="flex flex-col items-center">
                                <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mb-4" />
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">본인인증 진행중</h2>
                                <p className="text-gray-600 text-sm text-center">
                                    인증창을 열고 있습니다...<br />
                                    잠시만 기다려주세요.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Initial / Retry UI */}
                    {step === 'initial' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5">
                                        <MousePointerClick className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-amber-800">
                                            자동 실행이 차단되었습니다.
                                        </p>
                                        <p className="text-xs text-amber-600 mt-1">
                                            앱카드 모듈이 자동으로 열리지 않았습니다. 아래 버튼을 눌러 진행해주세요.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleStartLogin}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                            >
                                <Shield className="w-5 h-5" />
                                본인인증 시작하기
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* 앱카드 모듈 화면 렌더링 (팝업/오버레이 형태) */}
            {moduleVisible && authData.transNo && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="relative w-full h-full max-w-lg md:h-[600px] md:max-h-[90vh] bg-white md:rounded-xl overflow-hidden shadow-2xl flex flex-col">
                        <button
                            onClick={closeModule}
                            className="absolute top-4 right-4 z-50 p-2 bg-white rounded-full shadow-md text-gray-600 hover:text-gray-900"
                        >
                            <XCircle className="w-6 h-6" />
                        </button>
                        <object
                            ref={objectRef}
                            id="appCardModule"
                            type="text/html"
                            data={`${APPCARD_MODULE_URL}?transNo=${authData.transNo}&deviceType=${deviceType}`}
                            className="w-full h-full border-0"
                            title="App Card Authentication"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}